
import React, { useState, useEffect, useRef } from 'react';
import { recommendDish } from '../services/geminiService';
import { Language } from '../types';
import { AiIcon, CloseIcon, SendIcon } from './icons';
import Loader from './Loader';
import { t } from '../translations';
import { AnimatePresence, motion } from 'framer-motion';

interface AiRecommenderProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  onRecommend: (itemId: number) => void;
}

const AiRecommender: React.FC<AiRecommenderProps> = ({ isOpen, onClose, language, onRecommend }) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState<{ type: 'user' | 'ai' | 'error' | 'intro', text: string }[]>([]);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      setConversation([{type: 'intro', text: t('aiRecommender.intro', language)}]);
    } else {
      setConversation([]);
      setPrompt('');
    }
  }, [isOpen, language]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || isLoading) return;

    setIsLoading(true);
    setConversation(prev => [...prev, { type: 'user', text: prompt }]);

    try {
      const recommendedId = await recommendDish(prompt, language);
      if (recommendedId) {
        onRecommend(recommendedId);
        setConversation(prev => [...prev, { type: 'ai', text: t('aiRecommender.success', language) }]);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => onClose(), 2500);
      } else {
        throw new Error("Couldn't find a matching dish.");
      }
    } catch (err) {
      setConversation(prev => [...prev, { type: 'error', text: t('aiRecommender.error', language)}]);
    } finally {
      setIsLoading(false);
      setPrompt('');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          key="ai-recommender-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-end justify-center p-4 sm:items-center"
        >
          <motion.div 
            key="ai-recommender-content"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-[var(--bg-secondary)] squircle shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col border border-[var(--glass-border)]"
          >
            <header className="flex items-center justify-between p-5 border-b border-[var(--glass-border)] flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[var(--accent-color)] rounded-full text-white">
                    <AiIcon className="w-5 h-5"/>
                </div>
                <h2 className="text-xl font-bold font-serif text-[var(--text-primary)]">{t('aiRecommender.title', language)}</h2>
              </div>
              <button onClick={onClose} className="p-2 bg-[var(--bg-tertiary)] rounded-full text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                <CloseIcon className="w-5 h-5" />
              </button>
            </header>
            
            <div className="flex-1 p-5 overflow-y-auto space-y-4">
              {conversation.map((msg, index) => (
                <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-5 py-3 rounded-2xl text-sm font-medium leading-relaxed shadow-sm ${
                    msg.type === 'user' ? 'bg-[var(--accent-color)] text-[var(--accent-text)] rounded-br-none' : 
                    msg.type === 'intro' ? 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] border border-[var(--glass-border)]' :
                    'bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-bl-none'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {isLoading && <Loader />}
            </div>

            <form onSubmit={handleSubmit} className="p-4 border-t border-[var(--glass-border)] flex-shrink-0 bg-[var(--bg-secondary)] rounded-b-[30px]">
              <div className="relative">
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={t('aiRecommender.placeholder', language)}
                  className="w-full pl-5 pr-12 py-4 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-2xl placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] transition-all"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={isLoading || !prompt.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[var(--accent-color)] text-[var(--accent-text)] p-2.5 rounded-xl hover:brightness-110 disabled:opacity-50 transition shadow-md"
                >
                  <SendIcon className="w-5 h-5" />
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AiRecommender;
